/**
 * How the analysis charts cluster time (the viewer's clock, whole hours).
 *
 * The server answers per hour of the viewer's time zone, keyed
 * 'YYYY-MM-DDTHH' (a bare 'YYYY-MM-DD' reads as that day's 00 hour). Here
 * those hours are grouped into hours, 6 hours, 12 hours or days — always on
 * whole boundaries of the viewer's day: 08:00, 09:00…; 00–06, 06–12, 12–18,
 * 18–24; 00–12, 12–24 — and laid out from the first bucket with data to the
 * one holding now, with empty buckets in between drawn as empty, not skipped
 * (and at least MIN_POINTS of them, reaching back from now).
 *
 * Each window has a default (24 hours → hourly, 7 days → 6 hours, longer →
 * daily). When that would leave only a few points — 30 days of window but
 * two days of data — it steps down to a finer grain until the chart has
 * enough to read. A chart's own "Cluster by" overrides it.
 */

export const SIZES = [
  { value: 'hour', label: 'By hour', hours: 1 },
  { value: '6h', label: 'Every 6 hours', hours: 6 },
  { value: '12h', label: 'Every 12 hours', hours: 12 },
  { value: 'day', label: 'By day', hours: 24 },
];
const HOURS = Object.fromEntries(SIZES.map((s) => [s.value, s.hours]));
const MIN_POINTS = 6;
export const MAX_POINTS = 400;
const SEP = '|';

export function viewerTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch (_) { return 'UTC'; }
}

const pad = (n) => String(n).padStart(2, '0');

/** A key as wall-clock milliseconds (UTC arithmetic on the viewer's wall clock). */
export function keyMs(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}))?/.exec(String(key || ''));
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0));
}
export function msKey(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}`;
}

/** The bucket an hour falls in: its start, as a key. */
export function bucketOf(key, size) {
  const d = new Date(keyMs(key));
  if (Number.isNaN(d.getTime())) return String(key);
  const h = HOURS[size] || 24;
  return msKey(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), Math.floor(d.getUTCHours() / h) * h));
}

/** An instant's hour on the viewer's clock (now, by default), as a key in `tz`. */
export function hourKeyAt(instant, tz) {
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
    }).formatToParts(date).map((p) => [p.type, p.value]));
    return `${parts.year}-${parts.month}-${parts.day}T${pad(Number(parts.hour) % 24)}`;
  } catch (_) {
    return msKey(date.getTime());
  }
}

/** Every bucket from the one holding `fromKey` to the one holding `toKey`. */
export function bucketsBetween(fromKey, toKey, size) {
  const step = (HOURS[size] || 24) * 3600 * 1000;
  const out = [];
  let at = keyMs(bucketOf(fromKey, size));
  const end = keyMs(bucketOf(toKey, size));
  if (!Number.isFinite(at) || !Number.isFinite(end)) return out;
  // Wall-clock arithmetic: a day is always 24 of the viewer's hours here.
  while (at <= end && out.length <= MAX_POINTS * 4) {
    out.push(msKey(at));
    at += step;
  }
  return out;
}

const firstOf = (keys) => [...keys].filter(Boolean).sort()[0];

/** How many buckets a chart would draw from its first data to now. */
export const pointsFor = (firstKey, nowKey, size) => (firstKey ? bucketsBetween(firstKey, nowKey, size).length : 0);

export function defaultSize(days) {
  if (days <= 1) return 'hour';
  if (days <= 7) return '6h';
  return 'day';
}

/** The window's default, made finer while it would draw too few points, coarser while too many. */
export function autoSize(keys, days, nowKey) {
  const first = firstOf(keys);
  const order = SIZES.map((s) => s.value);
  let i = order.indexOf(defaultSize(days));
  if (!first) return order[i];
  while (i > 0 && pointsFor(first, nowKey, order[i]) < MIN_POINTS) i -= 1;
  while (i < order.length - 1 && pointsFor(first, nowKey, order[i]) > MAX_POINTS) i += 1;
  return order[i];
}

/** The sizes worth offering for these keys: those that draw no more than MAX_POINTS. */
export function sizesFor(keys, nowKey) {
  const first = firstOf(keys);
  return SIZES.filter((s) => !first || pointsFor(first, nowKey, s.value) <= MAX_POINTS);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** '2026-09-22T14' → '22 Sep 14:00' (or '22 Sep' by day). */
export function bucketLabel(key, size) {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}))?/.exec(String(key || ''));
  if (!m) return String(key || '');
  const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  return size === 'day' ? day : `${day} ${m[4] || '00'}:00`;
}

/**
 * Rows keyed by `day` (an hour key) regrouped into `size` buckets: numbers
 * summed per bucket and per `groupBy` field, one row per group per bucket.
 */
export function rebucket(rows = [], size, groupBy = []) {
  const out = new Map();
  for (const r of rows) {
    const b = bucketOf(r.day, size);
    const k = [b, ...groupBy.map((g) => r[g])].join(SEP);
    const acc = out.get(k) || { ...Object.fromEntries(groupBy.map((g) => [g, r[g]])), day: b };
    for (const [f, v] of Object.entries(r)) {
      if (f === 'day' || groupBy.includes(f)) continue;
      if (typeof v === 'number') acc[f] = (acc[f] || 0) + v;
    }
    out.set(k, acc);
  }
  return [...out.values()];
}

/**
 * The shared part of every time chart: which size it draws at (its own
 * pick, else the window's auto choice), the sizes it may offer, and the
 * buckets from its first data to now.
 */
export function clusterFor({ keys, days, nowKey, picked }) {
  const offered = sizesFor(keys, nowKey);
  const auto = autoSize(keys, days, nowKey);
  const size = picked && offered.some((s) => s.value === picked) ? picked : auto;
  const first = firstOf(keys);
  let buckets = first ? bucketsBetween(first, nowKey, size) : [];
  // Even the finest grain can leave a burst of data as one or two slots
  // filling the width; the axis then reaches back from now to MIN_POINTS
  // buckets, so the data sits in its context rather than filling the chart.
  if (buckets.length && buckets.length < MIN_POINTS) {
    const step = (HOURS[size] || 24) * 3600 * 1000;
    const start = keyMs(bucketOf(nowKey, size)) - (MIN_POINTS - 1) * step;
    buckets = bucketsBetween(msKey(Math.min(start, keyMs(buckets[0]))), nowKey, size);
  }
  return { size, auto, offered, buckets };
}
